import { BREAKPOINTS } from '@app/constants/breakpoints';
import { cn } from '@app/utils/cn';
import { useIsScreenSize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { Tables } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '../Icons';

import { TableHeader } from './components/header/TableHeader';
import { TableBody } from './components/TableBody';
import { TableCardFooter } from './components/TableCardFooter';
import { TableFooter } from './components/TableFooter';

const DEFAULT_SKELETON_ROWS = 20;

export const Table = <T,>({
  table,
  dataTestId,
  renderSubComponent,
  isLoading = false,
  skeletonRows = DEFAULT_SKELETON_ROWS,
  noDataMessage,
  extraBodyRow,
  renderCard,
  renderSkeletonCard,
  variant,
  className,
  rowHeight,
  getRowMarkerColor,
  fixedLayout = false,
  mobileBreakpoint = BREAKPOINTS.LG,
}: Tables.TableProps<T>) => {
  const { t } = useTranslation();
  const isMobile = useIsScreenSize(mobileBreakpoint);

  const skeletonRowItems = React.useMemo(() => Array.from({ length: skeletonRows }, () => undefined), [skeletonRows]);

  if (isMobile && isDefined(renderCard)) {
    return (
      <div>
        <div className={cn('grid grid-cols-1 gap-2 sm:grid-cols-table-mobile', className)}>
          {isLoading ?
            skeletonRowItems.map((_, index) => renderSkeletonCard?.(index))
          : table.getRowModel().rows.map((row, index) => renderCard(row, index))}
        </div>
        <TableCardFooter table={table} isLoading={isLoading} />
      </div>
    );
  }

  return (
    <div className="relative clear-both overflow-x-auto">
      <table
        data-testid={dataTestId}
        className={cn(
          'w-full border-separate border-spacing-x-0 border-spacing-y-2',
          {
            'table-fixed': fixedLayout,
          },
          className,
        )}
      >
        <colgroup>
          {table
            .getAllLeafColumns()
            .filter(column => column.getIsVisible())
            .map((column, index) => (
              <col
                key={index}
                style={{
                  minWidth: column.columnDef.meta?.minWidth,
                  width: column.columnDef.meta?.width,
                }}
              />
            ))}
        </colgroup>
        <TableHeader table={table} />
        {(isLoading || table.getRowCount() !== 0) && (
          <>
            <TableBody
              table={table}
              renderSubComponent={renderSubComponent}
              extraBodyRow={extraBodyRow}
              isLoading={isLoading}
              skeletonRows={skeletonRows}
              variant={variant}
              rowHeight={rowHeight}
              getRowMarkerColor={getRowMarkerColor}
            />
            <TableFooter table={table} isLoading={isLoading} />
          </>
        )}
      </table>
      {!isLoading && table.getRowCount() === 0 && (
        <div className="py-6 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 flex-col items-center justify-center rounded-full bg-neutral-98 p-4 text-center text-neutral-40">
            <Box className="size-8 shrink-0" />
          </div>
          <p className="heading-3">{isDefined(noDataMessage) ? noDataMessage : t('common:no_data', { returnObjects: false })}</p>
        </div>
      )}
    </div>
  );
};
